import React, { useState, Suspense, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import MuiAlert from '@material-ui/lab/Alert';

import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';

import { /*LogotipNapisSvg,*/ ObcinskiNosSvg, DefaultLogoSvg } from 'icons';
import {
	notificationState,
	formMetadataState,
	themeState,
	isInAppNameState,
	coordsState,
	selectedPageState,
	isRestrictedState,
} from 'state';
import { ValidateIfLocationInAppName } from 'api';
import DomainRoutes, { routes, join } from 'routes/DomainRoutes';
import { AppBarAvatar, Footer, Loader } from 'common/components';
import { useStyles } from './MainStyles';
import { isRestricted } from '../../../utils';

const Logos = {
	ObcinskiNosSvg,
};

export function Main() {
	// state: notification
	const { timestamp, message, severity } = useRecoilValue(notificationState);

	// state: data about the form
	const {
		name,
		url,
		iconName,
		primaryColor,
		secondaryColor,
		useGeolocation,
		useRateLimiting,
		useGeofence,
		sysAppname,
	} = useRecoilValue(formMetadataState);

	const setRestriction = useSetRecoilState(isRestrictedState);

	// state: is user in required app
	const [inAppName, setInAppName] = useRecoilState(isInAppNameState);

	// state: coordinations
	const { longitude, latitude, userAllowed } = useRecoilValue(coordsState);

	// state: set theme
	const setTheme = useSetRecoilState(themeState);

	// use classes (material ui)
	const classes = useStyles();

	// use location (react router)
	const location = useLocation();

	const Logo = useMemo(() => {
		return Logos.hasOwnProperty(iconName) ? Logos[iconName] : DefaultLogoSvg;
	}, [iconName]);

	const routeIndex = useMemo(() => {
		const index = routes.findIndex(
			({ path }) => `/${url}/${path}`.indexOf(location.pathname) > -1
		);
		return index > -1 ? index : 0;
	}, [location.pathname, url]);

	//const [value, setValue] = useState(routeIndex);
	const [tab, setTab] = useRecoilState(selectedPageState);

	const [showSnackBar, setShowSnackBar] = useState(false);

	useEffect(() => {
		setRestriction(isRestricted(useRateLimiting, url));
	}, [url, setRestriction, useRateLimiting]);

	useEffect(() => {
		const mounted = async () => {
			try {
				const { data } = await ValidateIfLocationInAppName(
					url,
					latitude,
					longitude
				);
				setInAppName(data);
			} catch (error) {
				alert(JSON.stringify(error));
			}
		};

		longitude && latitude && url && mounted();
	}, [longitude, latitude, url, setInAppName]);

	useEffect(() => {
		const pc =
			primaryColor !== null && primaryColor.length === 7 ? primaryColor : null;
		const sc =
			secondaryColor !== null && secondaryColor.length === 7
				? secondaryColor
				: null;

		//				newTheme.palette.primary.main = primaryColor;

		setTheme((prevState) => ({
			palette: {
				...prevState.palette,
				primary: {
					main: pc ? pc : prevState.palette.primary.main,
				},
				secondary: {
					main: sc ? sc : prevState.palette.secondary.main,
				},
			},
		}));
	}, [primaryColor, secondaryColor, setTheme]);

	// useEffect for title of the page
	useEffect(() => {
		window.document.title = name;
	}, [name]);

	// useEffect for tabs
	useEffect(() => {
		setTab(routeIndex);
	}, [location, routeIndex, setTab]);

	// useEffect for notifications
	useEffect(() => {
		if (message !== '') {
			setShowSnackBar(true);
		} else {
			handleClose();
		}
	}, [timestamp, message]);

	const onTabsChange = (_, newValue) => {
		setTab(newValue);
	};

	const handleClose = () => {
		setShowSnackBar(false);
	};

	const Notification = useMemo(
		() => (
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				open={showSnackBar}
				onClose={handleClose}
				autoHideDuration={5000}
				key={new Date().getMilliseconds()}
				action={
					<IconButton
						aria-label="close"
						color="inherit"
						className={classes.close}
						onClick={handleClose}>
						<CloseIcon />
					</IconButton>
				}>
				<MuiAlert
					elevation={6}
					variant="filled"
					onClose={handleClose}
					severity={severity}>
					{message}
				</MuiAlert>
			</Snackbar>
		),
		[severity, message, showSnackBar, classes.close]
	);

	return (
		<Container maxWidth="lg" disableGutters>
			<CssBaseline />
			<div className={classes.root}>
				<AppBar className={classes.header} position="relative">
					<Toolbar>
						<Logo className={classes.svgLogo} />
						<Typography gutterBottom className={classes.title}>
							{name}
						</Typography>
						<AppBarAvatar
							userAllowed={userAllowed}
							inAppName={inAppName}
							sysAppname={sysAppname}
							hasGeofence={useGeofence}
							hasGeolocation={useGeolocation}
							url={url}
						/>
					</Toolbar>

					<Tabs
						value={tab}
						onChange={onTabsChange}
						aria-label="Meni"
						variant="scrollable"
						scrollButtons="on">
						{routes.map(({ label, path, icon }, i) => (
							<Tab
								key={i}
								label={label}
								icon={icon}
								component={Link}
								to={join(url, path)}
							/>
						))}
					</Tabs>
				</AppBar>
				<Grid container>
					<Grid item xs={12}>
						<Paper className={classes.paper} elevation={3}>
							<>
								<Suspense fallback={<Loader />}>
									<DomainRoutes />
								</Suspense>
							</>
						</Paper>
					</Grid>
				</Grid>
				<Footer classes={classes} />
			</div>
			{Notification}
		</Container>
	);
}
